import { useMemo, useState } from 'react';
import type { ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@standardsdigital/ui-components/Button';
import Heading, { HeadingSize } from '@standardsdigital/ui-components/Heading';
import Select from '@standardsdigital/ui-components/Select';
import SelectItem from '@standardsdigital/ui-components/SelectItem';
import {
  useCreateCustomSubscriptionConfigurationMutation,
  useListAvailableCustomSubscriptionConfigurationNsbsQuery,
  useListCustomSubscriptionConfigurationsQuery,
} from '@/redux/services/generate/configurations/customSubscriptionConfigurations';
import { useToast } from '@/util/hooks';
import styles from './ConfigurationSelector.module.css';

interface ConfigurationSelectorProps {
  selectedConfigurationId: string;
  onSelectConfiguration: (configurationId: string) => void;
}

function ConfigurationSelector({
  selectedConfigurationId,
  onSelectConfiguration,
}: Readonly<ConfigurationSelectorProps>) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: configurations = [], isLoading: isLoadingConfigurations } =
    useListCustomSubscriptionConfigurationsQuery();
  const { data: nsbs = [], isLoading: isLoadingNsbs } =
    useListAvailableCustomSubscriptionConfigurationNsbsQuery();
  const [
    createCustomSubscriptionConfiguration,
    { isLoading: isAddingConfiguration },
  ] = useCreateCustomSubscriptionConfigurationMutation();
  const [selectedNsbRecId, setSelectedNsbRecId] = useState<number | null>(null);
  const [showNsbValidationError, setShowNsbValidationError] = useState(false);

  const availableNsbsToAdd = useMemo(() => {
    const usedNsbIds = new Set(
      configurations.map((configuration) => configuration.nsbRecId),
    );

    return nsbs.filter((nsb) => !usedNsbIds.has(nsb.id));
  }, [configurations, nsbs]);

  const selectedNsbRecIdToAdd = useMemo(() => {
    if (availableNsbsToAdd.length === 0) {
      return null;
    }

    const hasSelectedNsb = availableNsbsToAdd.some(
      (nsb) => nsb.id === selectedNsbRecId,
    );

    return hasSelectedNsb ? selectedNsbRecId : null;
  }, [availableNsbsToAdd, selectedNsbRecId]);

  const onSelectNsbRecId: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const nextValue = event.target.value;

    setSelectedNsbRecId(nextValue === '' ? null : Number(nextValue));
    setShowNsbValidationError(false);
  };

  const onAddConfigurationClick = async () => {
    if (selectedNsbRecIdToAdd === null) {
      if (availableNsbsToAdd.length > 0) {
        setShowNsbValidationError(true);
      }

      return;
    }

    try {
      const createdConfiguration = await createCustomSubscriptionConfiguration({
        createCustomSubscriptionConfigurationRequest: {
          nsbRecId: selectedNsbRecIdToAdd,
        },
      }).unwrap();

      onSelectConfiguration(createdConfiguration.id);
      setSelectedNsbRecId(null);
      toast.success(t('customSubscriptionConfiguration.successfullyCreated'));
    } catch {
      // Errors are handled in middleware.
    }
  };

  const isConfigurationActionDisabled =
    isAddingConfiguration || isLoadingConfigurations || isLoadingNsbs;

  return (
    <section className={styles.panel}>
      <Heading size={HeadingSize.Sm} level={2}>
        {t('customSubscriptionConfiguration.selectorTitle')}
      </Heading>
      <div className={styles.addConfigurationRow}>
        <Select
          className={styles.addSelect}
          label={t('customSubscriptionConfiguration.addNsb')}
          value={selectedNsbRecId === null ? '' : String(selectedNsbRecId)}
          onChange={onSelectNsbRecId}
          disabled={isConfigurationActionDisabled}
          message={
            showNsbValidationError
              ? t('customSubscriptionConfiguration.selectSdoValidation')
              : undefined
          }
          severity={showNsbValidationError ? 'danger' : undefined}
        >
          {availableNsbsToAdd.length === 0 ? (
            <SelectItem value="">
              {t('customSubscriptionConfiguration.noAvailableNsbs')}
            </SelectItem>
          ) : (
            <>
              <SelectItem value="">
                {t('customSubscriptionConfiguration.selectAnOption')}
              </SelectItem>
              {availableNsbsToAdd.map((nsb) => (
                <SelectItem value={nsb.id} key={nsb.id}>
                  {nsb.nsbId}
                </SelectItem>
              ))}
            </>
          )}
        </Select>
        <div className={styles.addButtonWrapper}>
          <Button
            type="button"
            onClick={onAddConfigurationClick}
            disabled={isConfigurationActionDisabled}
          >
            {t('customSubscriptionConfiguration.addConfiguration')}
          </Button>
        </div>
      </div>
      <div className={styles.existingConfigurationsWrapper}>
        <div className={styles.existingConfigurations}>
          {configurations.length === 0 ? (
            <span>{t('customSubscriptionConfiguration.noConfigurations')}</span>
          ) : (
            configurations.map((configuration) => (
              <Button
                variant="text"
                key={configuration.id}
                onClick={() => onSelectConfiguration(configuration.id)}
                className={`${styles.button} ${selectedConfigurationId === configuration.id ? styles.active : ''}`}
              >
                {configuration.nsbName}
              </Button>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

export default ConfigurationSelector;
